import { InfoCircleOutlined, SearchOutlined } from "@ant-design/icons";
import { useDebounce } from "use-debounce";

import { Button, Empty, Input, Spin, message } from "antd";
import dayjs from "dayjs";
import { FC, useEffect, useState } from "react";

import TransactionDetailsModal from "@/components/TransactionDetailsModal";
import Breadcrumbs from "@/components/common/Breadcrumb";
import { IMAGES } from "@/constant";
import styles from "@/pages/Transaction/Transaction.module.css";
import {
  generatePledgeCreationEInvoice,
  generateRedemptionEInvoice,
  generateRenewalEInvoice,
} from "@/services/Payment/PaymentApis";
import { getTransactionsByUser } from "@/services/Pledge/PledgeApis";
import { useAuthStore } from "@/stores/authStore";
import { TransactionInterface } from "@/types/pledgeService";
import { formatAmount } from "@/utils/formatAmount";
import { printPdfFromUrl } from "@/utils/printPdfFromUrl";
import { transactionDisplayAmount, transactionDisplayKey } from "@/utils/transactionDisplay";

const displayListAmount = (item: TransactionInterface): string => {
  if (item.renewal_stage) return formatAmount(transactionDisplayAmount(item) ?? 0);
  const isRenewal = item.payment_type?.toLowerCase() === "renewal";
  const isDownloanRenewal =
    isRenewal && item.pledge?.is_downloan === true && Number(item.downloan_amount) > 0;
  const isUploanReceipt =
    isRenewal && item.pledge?.is_uploan === true && Number(item.uploan_amount) > 0;
  const amount = isUploanReceipt
    ? Number(item.uploan_amount)
    : isRenewal
      ? Number(item.interest_amount ?? 0) +
        Number(item.pledge?.admin_fee ?? item.admin_fee ?? 0) +
        (isDownloanRenewal ? Number(item.downloan_amount) : 0)
      : item.paid_amount;

  return amount ? formatAmount(Number(amount)) : "-";
};

const TransactionsPage: FC = () => {
  const { isMalaysia, isSingapore } = useAuthStore();
  const currency = useAuthStore((state) => state.currency);
  const currencySuffix = currency ? ` (${currency})` : "";
  const [searchKey, setSearchKey] = useState<string>("");
  const [transactionListData, setTransactionListData] = useState<TransactionInterface[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const limit = 10;
  const [invoiceMap, setInvoiceMap] = useState<Record<string, any>>({});
  const [selectedTransaction, setSelectedTransaction] = useState<TransactionInterface | null>(null);

  const [debouncedSearch] = useDebounce(searchKey, 500);

  const getPledgeTransactionsListHelper = async (pageNo: number): Promise<void> => {
    try {
      setLoading(true);
      const payload = {
        page: pageNo,
        limit,
        search_key: debouncedSearch,
        group_renewals: true,
      };

      const response = await getTransactionsByUser(payload);

      setTransactionListData((prev) => [...prev, ...response.data]);

      setHasMore(response.page < response.totalPages);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setTransactionListData([]);
    setPage(1);
    setHasMore(true);

    void getPledgeTransactionsListHelper(1);
  }, [debouncedSearch]);

  const loadMoreHelper = (): void => {
    if (!hasMore) return;

    const nextPage = page + 1;
    setPage(nextPage);
    void getPledgeTransactionsListHelper(nextPage);
  };

  const handleEInvoiceRequest = async (item: TransactionInterface) => {
    try {
      setLoading(true);
      let response;

      if (item.payment_type === "pledge creation") {
        response = await generatePledgeCreationEInvoice(item.pledge.id, false);
      } else if (item.payment_type === "renewal") {
        response = await generateRenewalEInvoice(item.pledge.id);
      } else if (item.payment_type === "redemption") {
        response = await generateRedemptionEInvoice(item.pledge.id, false);
      } else {
        return;
      }

      setInvoiceMap((prev) => ({
        ...prev,
        [transactionDisplayKey(item)]: response,
      }));

      message.success("E-Invoice requested successfully");
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handlePrintInvoice = async (item: TransactionInterface) => {
    try {
      const invoiceData = invoiceMap[transactionDisplayKey(item)];

      if (!invoiceData?.pdf_url) {
        message.warning("Please request E-Invoice first");
        return;
      }

      await printPdfFromUrl(invoiceData.pdf_url);
    } catch (error) {
      console.error(error);
    }
  };

  const popupContent = <div className={styles.popoverContainer} />;

  return (
    <Spin spinning={loading}>
      <div className={styles.container}>
        <div className={styles.customBreadCrumbs}>
          <Breadcrumbs items={[{ label: "Transactions" }]} />
        </div>
        <div className={styles.mainContainer}>
          <div className={styles.tenantRightActions}>
            <Input
              placeholder="Search by ticket,item"
              suffix={<SearchOutlined className={styles.searchInputIcon} />}
              className={styles.searchInput}
              allowClear
              value={searchKey}
              onChange={(e) => setSearchKey(e.target.value)}
            />
          </div>

          {transactionListData?.length ? (
            transactionListData.map((item: TransactionInterface) => (
              <div key={transactionDisplayKey(item)} className={styles.card}>
                <div className={styles.cardFullContainer}>
                  <div className={styles.leftSideItems}>
                    <div
                      className={`${item?.payment_type == "redemption" ? styles.iconArrowDiv : styles.iconReloadDiv}`}
                    >
                      <img
                        src={
                          item?.payment_type == "redemption" ? IMAGES.TOP_ARROW : IMAGES.RELOAD_ICON
                        }
                        alt="transaction-icon"
                        className={styles.iconSize}
                      />
                    </div>
                    <div className={styles.type}>
                      {item.payment_type?.[0].toUpperCase() + item.payment_type?.slice(1)}
                    </div>
                    <div className={styles.itemType}>
                      <span>
                        {item?.pledge?.ticket_alpha_code}
                        {/* {item?.item} */}
                      </span>
                    </div>
                    <div className={styles.date}>
                      <div>Ref - {item?.ref_no}</div>
                      <div>
                        {" "}
                        {item?.updated_at
                          ? dayjs(item?.updated_at).format("DD MMM YYYY, hh:mm A")
                          : "-"}
                      </div>
                    </div>
                  </div>
                  <div className={styles.rightSideItems}>
                    <div className={styles.amount}>
                      {displayListAmount(item)} {currencySuffix}
                      <InfoCircleOutlined
                        className={styles.infoIcon}
                        onClick={() => setSelectedTransaction(item)}
                      />
                    </div>
                    <div
                      className={`${styles.status} ${
                        {
                          failed: styles.statusFailed,
                          cancelled: styles.statusFailed,
                          pending: styles.statusPending,
                        }[item.payment_status?.toLowerCase()] ?? styles.statusSuccess
                      }`}
                    >
                      {item.payment_status
                        ? item.payment_status.charAt(0).toUpperCase() + item.payment_status.slice(1)
                        : "-"}
                    </div>

                    {isMalaysia ? (
                      <>
                        {item.updated_at &&
                        dayjs(item.updated_at).isAfter(dayjs().subtract(3, "day")) ? (
                          !invoiceMap[transactionDisplayKey(item)]?.pdf_url &&
                          item.payment_status === "completed" ? (
                            <span
                              className={styles.actionItem}
                              onClick={() => handleEInvoiceRequest(item)}
                            >
                              REQUEST E-INVOICE
                            </span>
                          ) : invoiceMap[transactionDisplayKey(item)]?.pdf_url ? (
                            <span
                              className={styles.actionItem}
                              onClick={() => handlePrintInvoice(item)}
                            >
                              PRINT
                            </span>
                          ) : (
                            <span className={styles.actionPlaceholder} />
                          )
                        ) : (
                          <span className={styles.actionPlaceholder} />
                        )}
                      </>
                    ) : null}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <Empty />
          )}

          {hasMore ? (
            <div className={styles.button}>
              <Button className={styles.clearButton} onClick={loadMoreHelper}>
                Load More
              </Button>
            </div>
          ) : null}
        </div>
      </div>

      {selectedTransaction && (
        <TransactionDetailsModal
          open={Boolean(selectedTransaction)}
          onClose={() => setSelectedTransaction(null)}
          transaction={selectedTransaction}
          currencySuffix={currencySuffix}
        />
      )}
    </Spin>
  );
};
export default TransactionsPage;
